import { downloadFile } from '@app/hooks/useDownload';
import { SignatureRequestToken, Subcontractor } from '@app/utils/parseJwt';
import OutputView from '@components/block/OutputView/OutputView';
import { NotificationModal } from '@components/ui';
import { useOpenQuestionnaireMutation } from '@dieterApi/questionnaire/useOpenQuestionnaireMutation';
import { useQuestionnaireDownloadPdfV2Mutation } from '@dieterApi/questionnaire/useQuestionnaireDownloadPdfV2Mutation';
import { Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs } from '@mui/material';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Download } from 'lucide-react';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  token: string;
  tokenInfo: SignatureRequestToken;
}

export function AttachmentsModal({ open, onClose, tokenInfo, token }: Props) {
  const [tab, setTab] = useState(0);
  const tomsQuestionnaireId = tokenInfo?.tomsQuestionnaireId;
  const subcontractors = tokenInfo?.subcontractors || [];
  const hasToms = !!tomsQuestionnaireId;
  const hasSubcontractors = subcontractors && subcontractors?.length;
  const tabs = [
    ...(hasToms
      ? [
          {
            label: 'TOMs',
            content: <TomsAttachment tomsQuestionnaireId={tomsQuestionnaireId} token={token} />,
            hasDownload: true,
          },
        ]
      : []),
    ...(hasSubcontractors
      ? [
          {
            label: 'Subunternehmerliste',
            content: <SubcontractorAttachment subcontractors={subcontractors} />,
            hasDownload: true,
          },
        ]
      : []),
  ];

  const [openQuestionnaire] = useOpenQuestionnaireMutation();
  const [downloadPdf, { loading: downloadingPdf }] = useQuestionnaireDownloadPdfV2Mutation();
  const [downloadingSubcontractors, setDownloadingSubcontractors] = useState(false);

  const handleDownloadToms = async () => {
    if (!tomsQuestionnaireId) return;
    try {
      // First, validate/recreate questionnaire on legalOS if needed
      await openQuestionnaire({
        variables: { localQuestionnaireId: tomsQuestionnaireId, token },
      });
      // V2 mutation uses local ID for HTML-first rendering
      const result = await downloadPdf({
        variables: {
          id: tomsQuestionnaireId,
          token,
          fileName: `${tokenInfo.companyName} - TOMs.pdf`,
        },
      });
      const downloadData = result?.data?.questionnaireDownloadPdfV2;
      if (downloadData) {
        downloadFile(downloadData);
      }
    } catch (error) {
      console.error('Error downloading TOMS:', error);
    }
  };

  const handleDownloadSubcontractors = () => {
    setDownloadingSubcontractors(true);
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      const title = `${tokenInfo.companyName} - Subunternehmer-Liste`;
      const lines = doc.splitTextToSize(title, 180);
      doc.text(lines, 14, 20);

      const tableData = subcontractors.map((s) => [s.name, s.address || '', s.details || '']);
      autoTable(doc, {
        head: [['Name', 'Adresse', 'Details zur Datenweitergabe']],

        body: tableData,
        startY: 40,
        styles: { fontSize: 12 },
        headStyles: { fillColor: [120, 120, 120] },
      });

      doc.save(`${tokenInfo.companyName} - Subunternehmer-Liste.pdf`);
    } finally {
      setDownloadingSubcontractors(false);
    }
  };

  const handleDownload = () => {
    const currentTab = tabs[tab];
    if (currentTab?.label === 'TOMs') {
      handleDownloadToms();
    } else if (currentTab?.label === 'Subunternehmerliste') {
      handleDownloadSubcontractors();
    }
  };

  const isDownloading = downloadingPdf || downloadingSubcontractors;

  if (tabs.length === 0) return null;
  return (
    <NotificationModal open={open} onClose={onClose} maxWidth={'1000px'}>
      <div className="flex flex-col gap-4 p-6 min-w-[300px] h-[90vh]">
        <div className="flex justify-between items-center">
          <h3 className="font-medium text-xl">Anlagen</h3>
          {tabs[tab]?.hasDownload && (
            <button
              className="underline text-primary-root px-3 py-1 rounded text-sm flex items-center gap-2"
              onClick={handleDownload}
              disabled={isDownloading}
              data-testid="button-download-attachment"
              style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
            >
              <Download size={14} />
              {isDownloading ? 'Wird heruntergeladen...' : 'Herunterladen'}
            </button>
          )}
        </div>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {tabs.map((t, i) => (
            <Tab key={t.label} label={t.label} />
          ))}
        </Tabs>
        <div className="mt-4 min-h-[120px]">{tabs[tab]?.content}</div>
      </div>
    </NotificationModal>
  );
}

function TomsAttachment({ tomsQuestionnaireId, token }: { tomsQuestionnaireId: string; token: string }) {
  return (
    <div>
      <OutputView localQuestionnaireId={tomsQuestionnaireId} token={token} naked={true} />
    </div>
  );
}

function SubcontractorAttachment({ subcontractors }: { subcontractors: Subcontractor[] }) {
  if (!subcontractors || subcontractors.length === 0) {
    return <div>Keine Subunternehmer vorhanden.</div>;
  }
  return (
    <TableContainer component={Paper} sx={{ width: '100%', ml: 0, boxShadow: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', background: '#e0e0e0' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold', background: '#e0e0e0' }}>Adresse</TableCell>
            <TableCell sx={{ fontWeight: 'bold', background: '#e0e0e0' }}>Details zur Datenweitergabe</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subcontractors.map((s, idx) => (
            <TableRow key={idx}>
              <TableCell>{s.name}</TableCell>
              <TableCell>{s.address || ''}</TableCell>
              <TableCell>{s.details || ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
